/**
 * B709 (a3) — REDEEM MY GIFT on the buyer's own session: `membership.gift.redeem`
 * by member number (the server's owner fence answers anyone else's gift as
 * absent). The answer is words in the panel; a redeemed gift re-reads the
 * portal so its cards and term appear. A signed-out session is sent to sign in.
 */
import { useCallback, useState } from "react";
import { ApiError } from "../lib/api.js";
import { giftRedeemRequest, redeemedWords, type GiftDraft } from "../lib/meMembership.js";
import type { GiftRedeeming } from "./meMembershipPage.js";

interface Caller {
  call(op: string, fields: Record<string, unknown>): Promise<unknown>;
}

export const REDEEM_NOT_CONFIRMED = "Your gift couldn't be redeemed just now. Nothing changed — try again in a moment.";

export function useGiftRedeem(opts: {
  readonly caller: Caller | undefined;
  readonly timeZone: string | undefined;
  readonly reread: () => void;
  readonly onSignedOut: () => void;
}): { readonly redeeming: GiftRedeeming | undefined; readonly redeem: ((memberNumber: string, draft: GiftDraft) => void) | undefined } {
  const { caller, timeZone } = opts;
  const [redeeming, setRedeeming] = useState<GiftRedeeming | undefined>(undefined);
  const redeem = useCallback(
    (memberNumber: string, draft: GiftDraft) => {
      if (caller === undefined) return;
      setRedeeming({ memberNumber, busy: true });
      void (async () => {
        try {
          const out = (await caller.call("membership.gift.redeem", { ...giftRedeemRequest(memberNumber, draft) })) as { member?: { term?: { end?: number } } };
          const end = out.member?.term?.end;
          setRedeeming({ memberNumber, busy: false, banner: { tone: "success", text: typeof end === "number" ? redeemedWords(end, timeZone) : redeemedWords(0, undefined) } });
          opts.reread();
        } catch (e: unknown) {
          if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
            opts.onSignedOut();
            return;
          }
          const said = e instanceof ApiError && e.code === "REFUSED" ? e.message : REDEEM_NOT_CONFIRMED;
          setRedeeming({ memberNumber, busy: false, banner: { tone: "danger", text: said } });
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the seams are fixed for the page's life
    [caller, timeZone],
  );
  return { redeeming, redeem: caller === undefined ? undefined : redeem };
}
